import apiClient from "./client";
import { unwrapApiResponse } from "../utils/apiError";
import { saveAxiosFile } from "../utils/download";

async function unwrap(request) {
  const response = await request;
  return unwrapApiResponse(response);
}

function createFileFormData(file) {
  const formData = new FormData();
  formData.append("file", file);
  return formData;
}

async function downloadFile(request, fallbackName) {
  const response = await request;
  saveAxiosFile(response, fallbackName);
}

function sanitizeQuestionPayload(data) {
  if (!data || typeof data !== "object") {
    return data;
  }

  const payload = { ...data };
  const questionType = payload.type;

  delete payload.textDirection;
  delete payload.optionsTextDirection;
  delete payload.dir;
  delete payload.direction;
  delete payload.isLtr;

  if (Array.isArray(payload.options)) {
    payload.options = payload.options.map((option) => {
      if (option == null || typeof option !== "object") {
        return option;
      }

      const next = { ...option };
      delete next.textDirection;
      delete next.dir;
      delete next.direction;
      delete next.isLtr;
      return next;
    });
  }

  if (questionType === "descriptive" || questionType === "instructional") {
    delete payload.options;
    delete payload.correctOptionIndex;
    delete payload.correctOptionId;
  }

  return payload;
}

function sanitizeGradePayload(data, { includeScore = true } = {}) {
  if (!data || typeof data !== "object") {
    return data;
  }

  if (!Array.isArray(data.answers)) {
    return data;
  }

  return {
    ...data,
    answers: data.answers.map(({ answerId, ...answer }) => {
      if (includeScore) return answer;

      const rest = { ...answer };
      delete rest.score;
      return rest;
    }),
  };
}

function createTeacherWorkAPI(resource, attachmentFallback, { supportsQuestionScore = true } = {}) {
  const base = `/teacher/${resource}`;

  return {
    getAll: (params) => unwrap(apiClient.get(base, { params })),

    getById: (id) => unwrap(apiClient.get(`${base}/${id}`)),

    create: (data) => unwrap(apiClient.post(base, data)),

    update: (id, data) => unwrap(apiClient.patch(`${base}/${id}`, data)),

    delete: (id) => unwrap(apiClient.delete(`${base}/${id}`)),

    publish: (id) => unwrap(apiClient.post(`${base}/${id}/publish`)),

    close: (id) => unwrap(apiClient.post(`${base}/${id}/close`)),

    getReport: (id) => unwrap(apiClient.get(`${base}/${id}/report`)),

    uploadAttachment: (id, file) =>
      unwrap(
        apiClient.post(`${base}/${id}/attachments`, createFileFormData(file), {
          headers: { "Content-Type": "multipart/form-data" },
        })
      ),

    deleteAttachment: (id, attachmentId) =>
      unwrap(apiClient.delete(`${base}/${id}/attachments/${attachmentId}`)),

    downloadAttachment: (id, attachmentId, filename = attachmentFallback) =>
      downloadFile(
        apiClient.get(`${base}/${id}/attachments/${attachmentId}/download`, {
          responseType: "arraybuffer",
        }),
        filename
      ),

    addQuestion: (id, data) =>
      unwrap(apiClient.post(`${base}/${id}/questions`, sanitizeQuestionPayload(data))),

    reorderQuestions: (id, questionIds) =>
      unwrap(apiClient.patch(`${base}/${id}/questions/reorder`, { questionIds })),

    updateQuestion: (id, questionId, data) =>
      unwrap(
        apiClient.patch(
          `${base}/${id}/questions/${questionId}`,
          sanitizeQuestionPayload(data)
        )
      ),

    deleteQuestion: (id, questionId) =>
      unwrap(apiClient.delete(`${base}/${id}/questions/${questionId}`)),

    uploadQuestionAttachment: (id, questionId, file) =>
      unwrap(
        apiClient.post(
          `${base}/${id}/questions/${questionId}/attachments`,
          createFileFormData(file),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
      ),

    deleteQuestionAttachment: (id, questionId, attachmentId) =>
      unwrap(
        apiClient.delete(`${base}/${id}/questions/${questionId}/attachments/${attachmentId}`)
      ),

    downloadQuestionAttachment: (id, questionId, attachmentId, filename = "question-attachment") =>
      downloadFile(
        apiClient.get(
          `${base}/${id}/questions/${questionId}/attachments/${attachmentId}/download`,
          { responseType: "arraybuffer" }
        ),
        filename
      ),

    uploadQuestionOptionAttachment: (id, questionId, optionId, file) =>
      unwrap(
        apiClient.post(
          `${base}/${id}/questions/${questionId}/options/${optionId}/attachment`,
          createFileFormData(file),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
      ),

    deleteQuestionOptionAttachment: (id, questionId, optionId) =>
      unwrap(
        apiClient.delete(
          `${base}/${id}/questions/${questionId}/options/${optionId}/attachment`
        )
      ),

    getSubmissions: (id) => unwrap(apiClient.get(`${base}/${id}/submissions`)),

    getSubmission: (id, submissionId) =>
      unwrap(apiClient.get(`${base}/${id}/submissions/${submissionId}`)),

    gradeSubmission: (id, submissionId, data) =>
      unwrap(
        apiClient.patch(
          `${base}/${id}/submissions/${submissionId}/grade`,
          sanitizeGradePayload(data, { includeScore: supportsQuestionScore })
        )
      ),
  };
}

export const assignmentAPI = createTeacherWorkAPI("homeworks", "homework-attachment");
export const challengeAPI = createTeacherWorkAPI("challenges", "challenge-attachment", {
  supportsQuestionScore: false,
});
